// Market data helpers
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Fetch Bitcoin data specifically for hero section
export async function fetchBitcoinPrice() {
  try {
    const response = await fetch(
      'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd&include_24hr_change=true'
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch Bitcoin price');
    }
    
    const data = await response.json();
    
    if (data.bitcoin) {
      return {
        price: data.bitcoin.usd,
        change24h: data.bitcoin.usd_24h_change || 0
      };
    }
    
    return null;
  } catch (error) {
    console.error('Failed to fetch Bitcoin price:', error);
    return null;
  }
}

// Define different coin groups to rotate through
const coinGroups = [
  {
    ids: 'bitcoin,ethereum,solana,cardano,ripple,polkadot',
    coins: ['bitcoin', 'ethereum', 'solana', 'cardano', 'ripple', 'polkadot']
  },
  {
    ids: 'binancecoin,avalanche-2,chainlink,polygon,uniswap,litecoin',
    coins: ['binancecoin', 'avalanche-2', 'chainlink', 'polygon', 'uniswap', 'litecoin']
  },
  {
    ids: 'dogecoin,shiba-inu,stellar,cosmos,tron,monero',
    coins: ['dogecoin', 'shiba-inu', 'stellar', 'cosmos', 'tron', 'monero']
  }
];

let currentGroupIndex = 0;

// Fetch real-time data from CoinGecko API
async function fetchRealTimeMarketData(groupIndex = 0) {
  try {
    const group = coinGroups[groupIndex];
    
    // Fetch data for the current group
    const response = await fetch(
      `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${group.ids}&order=market_cap_desc&per_page=10&page=1&sparkline=false&price_change_percentage=24h`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch market data');
    }
    
    const data = await response.json();
    
    // Symbol and icon mappings
    const symbolMap = {
      'bitcoin': 'BTC/USDT',
      'ethereum': 'ETH/USDT',
      'solana': 'SOL/USDT',
      'cardano': 'ADA/USDT',
      'ripple': 'XRP/USDT',
      'polkadot': 'DOT/USDT',
      'binancecoin': 'BNB/USDT',
      'avalanche-2': 'AVAX/USDT',
      'chainlink': 'LINK/USDT',
      'polygon': 'MATIC/USDT',
      'uniswap': 'UNI/USDT',
      'litecoin': 'LTC/USDT',
      'dogecoin': 'DOGE/USDT',
      'shiba-inu': 'SHIB/USDT',
      'stellar': 'XLM/USDT',
      'cosmos': 'ATOM/USDT',
      'tron': 'TRX/USDT',
      'monero': 'XMR/USDT'
    };
    
    const iconMap = {
      'bitcoin': { bg: 'bg-orange-500', symbol: '₿' },
      'ethereum': { bg: 'bg-blue-500', symbol: 'Ξ' },
      'solana': { bg: 'bg-purple-500', symbol: 'S' },
      'cardano': { bg: 'bg-blue-400', symbol: 'A' },
      'ripple': { bg: 'bg-gray-400', symbol: 'X' },
      'polkadot': { bg: 'bg-pink-500', symbol: 'D' },
      'binancecoin': { bg: 'bg-yellow-500', symbol: 'B' },
      'avalanche-2': { bg: 'bg-red-500', symbol: 'A' },
      'chainlink': { bg: 'bg-blue-600', symbol: 'L' },
      'polygon': { bg: 'bg-purple-600', symbol: 'M' },
      'uniswap': { bg: 'bg-pink-500', symbol: 'U' },
      'litecoin': { bg: 'bg-gray-500', symbol: 'L' },
      'dogecoin': { bg: 'bg-yellow-600', symbol: 'D' },
      'shiba-inu': { bg: 'bg-orange-600', symbol: 'S' },
      'stellar': { bg: 'bg-gray-600', symbol: 'X' },
      'cosmos': { bg: 'bg-purple-700', symbol: 'A' },
      'tron': { bg: 'bg-red-600', symbol: 'T' },
      'monero': { bg: 'bg-orange-700', symbol: 'M' }
    };
    
    // Transform CoinGecko data to our format
    return data.map(coin => {
      return {
        symbol: symbolMap[coin.id] || `${coin.symbol.toUpperCase()}/USDT`,
        name: coin.name,
        base: coin.symbol.toUpperCase(),
        lastPrice: coin.current_price,
        priceChange24h: coin.price_change_percentage_24h || 0,
        volume24h: coin.total_volume || 0,
        icon: iconMap[coin.id] || { bg: 'bg-primary-600', symbol: coin.symbol[0].toUpperCase() }
      };
    });
  } catch (error) {
    console.error('Failed to fetch real-time market data:', error);
    return null;
  }
}

export async function loadMarkets() {
  try {
    // First try to fetch real-time data from CoinGecko
    const realTimeData = await fetchRealTimeMarketData(currentGroupIndex);
    
    if (realTimeData && realTimeData.length > 0) {
      renderMarketList(realTimeData);
      
      // Set up rotation through different coin groups every 10 seconds
      setInterval(async () => {
        currentGroupIndex = (currentGroupIndex + 1) % coinGroups.length;
        const updatedData = await fetchRealTimeMarketData(currentGroupIndex);
        if (updatedData) {
          // Add fade transition effect
          const container = document.getElementById('marketList');
          if (container) {
            container.style.opacity = '0.5';
            container.style.transition = 'opacity 0.5s ease';
            
            setTimeout(() => {
              renderMarketList(updatedData);
              container.style.opacity = '1';
            }, 500);
          }
        }
      }, 10000); // Rotate every 10 seconds
      return;
    }
    
    // Fallback to backend API if real-time data fails
    const response = await fetch(`${API_URL}/api/v1/markets`);
    const data = await response.json();
    
    if (data.markets) {
      renderMarketList(data.markets);
    }
  } catch (error) {
    console.error('Failed to load markets:', error);
  }
}

function renderMarketList(markets) {
  const container = document.getElementById('marketList');
  if (!container) return;
  
  container.innerHTML = '';
  
  markets.forEach(market => {
    const changeClass = market.priceChange24h >= 0 ? 'badge-success' : 'badge-error';
    const changeSign = market.priceChange24h >= 0 ? '+' : '';
    const icon = market.icon || { bg: 'bg-primary-600', symbol: market.base[0] };
    
    const row = document.createElement('tr');
    row.className = 'hover:bg-neutral-800 transition-colors';
    row.innerHTML = `
      <td class="px-6 py-4">
        <div class="flex items-center space-x-3">
          <div class="w-8 h-8 ${icon.bg} rounded-full flex items-center justify-center text-white font-bold">
            ${icon.symbol}
          </div>
          <div>
            <div class="font-medium">${market.symbol}</div>
            <div class="text-sm text-neutral-400">${market.name}</div>
          </div>
        </div>
      </td>
      <td class="px-6 py-4 text-right font-mono">$${formatPrice(market.lastPrice)}</td>
      <td class="px-6 py-4 text-right">
        <span class="${changeClass}">${changeSign}${market.priceChange24h.toFixed(2)}%</span>
      </td>
      <td class="px-6 py-4 text-right font-mono text-neutral-400">$${formatVolume(market.volume24h)}</td>
      <td class="px-6 py-4 text-right">
        <a href="/trading.html?market=${market.symbol.replace('/', '-')}" class="btn-primary text-sm">Trade</a>
      </td>
    `;
    
    container.appendChild(row);
  });
}

function formatPrice(price) {
  if (price >= 1000) {
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else if (price >= 1) {
    return price.toFixed(2);
  } else {
    return price.toFixed(6);
  }
}

function formatVolume(volume) {
  if (volume >= 1000000000) {
    return (volume / 1000000000).toFixed(2) + 'B';
  } else if (volume >= 1000000) {
    return (volume / 1000000).toFixed(1) + 'M';
  } else if (volume >= 1000) {
    return (volume / 1000).toFixed(1) + 'K';
  }
  return volume.toFixed(2);
}
